import { colors } from '@dexma/ui-components';
import { isDashboardFrame } from 'adapter/in/common/widget/widgetBuilder';
import type { Breakpoint } from 'adapter/in/dashboards/components/Grid';
import {
    A4_CONTENT_END_ROW,
    A4_CONTENT_START_ROW,
    A4_CELL_HEIGHT,
    A4_FOOTER_ROWS,
    A4_HEADER_ROWS,
    A4_ROWS_PER_PAGE,
    BREAKPOINTS,
    CELL_MARGIN,
    COLUMNS,
    RATIO,
} from 'adapter/in/dashboards/components/Grid/constants';
import { GridLayoutElement } from 'adapter/in/dashboards/components/generic/ReactGrid';
import { LayoutElementType, LayoutType } from 'domain/entity/layout';

export const calculateBreakpointDesktop = (containerWidth: number): LayoutType => {
    if (containerWidth >= BREAKPOINTS.DESKTOP) return LayoutType.DESKTOP;
    if (containerWidth >= BREAKPOINTS.TABLET) return LayoutType.TABLET;
    return LayoutType.MOBILE;
};

export const buildBreakpoint = (breakpoint: Breakpoint) => ({
    [breakpoint]: BREAKPOINTS[breakpoint],
});

export const calculateCellSize = (containerWidth: number, breakpoint: LayoutType): { width: number; height: number } => {
    if (containerWidth > 0) {
        const marginSlotsCount = COLUMNS[breakpoint] - 1;
        const totalHorizontalMargin = marginSlotsCount * CELL_MARGIN;
        const freeSpace = containerWidth - totalHorizontalMargin;
        const width = freeSpace / COLUMNS[breakpoint];
        return {
            width,
            height: breakpoint === LayoutType.A4 ? A4_CELL_HEIGHT : Math.round(width / RATIO),
        };
    }

    return { width: 0, height: 0 };
};

export const generateGridBackground = (
    background: boolean,
    cellSize: { width: number; height: number },
    margin: [number, number],
    columns: number,
    containerWidth: number
): string => {
    if (!background) return '';

    const XMLNS = 'http://www.w3.org/2000/svg';
    const [horizontalMargin, verticalMargin] = margin;
    const rowHeight = cellSize.height + verticalMargin;

    const padding = 1;
    const y = padding;
    const w = cellSize.width - padding * 2;
    const h = cellSize.height - padding * 2;

    const rectangles = Array.from({ length: columns }, (_, k: number) => {
        const x = k * (cellSize.width + horizontalMargin) + padding;
        return `<rect stroke='${colors.gray100}' stroke-width='2' rx='8' fill='${colors.gray200}' x='${x}' y='${y}' width='${w}' height='${h}'/>`;
    });

    const svg = [`<svg xmlns='${XMLNS}' width='${containerWidth}' height='${rowHeight}'>`, ...rectangles, `</svg>`].join('');

    return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

export const computeDashboardFrameRows = (totalRows: number) => {
    const rowsPerPage = A4_ROWS_PER_PAGE;
    const pages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

    const headers: number[] = [];
    const footers: number[] = [];

    for (let page = 0; page < pages; page += 1) {
        const start = page * rowsPerPage;
        const end = start + rowsPerPage - 1;
        headers.push(start);
        footers.push(end);
    }

    return { headers, footers };
};

export const autoInsertHeadersAndFooters = (layout: GridLayoutElement[], totalRows: number): GridLayoutElement[] => {
    const { headers, footers } = computeDashboardFrameRows(totalRows);

    const layoutWithFrame = [...layout];

    headers.forEach((row, pageIndex) => {
        const id = `__header__${pageIndex}`;
        if (!layoutWithFrame.some((w) => w.i === id)) {
            layoutWithFrame.push({
                i: id,
                x: 0,
                y: row,
                w: COLUMNS[LayoutType.A4],
                h: 2,
                static: true,
                type: LayoutElementType.RICH_TEXT,
                minHeight: 2,
                maxHeight: 2,
                minWidth: COLUMNS[LayoutType.A4],
                maxWidth: COLUMNS[LayoutType.A4],
            });
        }
    });

    footers.forEach((row, pageIndex) => {
        const id = `__footer__${pageIndex}`;
        if (!layoutWithFrame.some((w) => w.i === id)) {
            layoutWithFrame.push({
                i: id,
                x: 0,
                y: row,
                w: COLUMNS[LayoutType.A4],
                h: 1,
                static: true,
                type: LayoutElementType.RICH_TEXT,
                minHeight: 1,
                maxHeight: 1,
                minWidth: COLUMNS[LayoutType.A4],
                maxWidth: COLUMNS[LayoutType.A4],
            });
        }
    });

    return layoutWithFrame;
};

export const normalizeA4Layout = (layout: GridLayoutElement[]): GridLayoutElement[] =>
    layout.map((el) => {
        const pageIndex = Math.floor(el.y / A4_ROWS_PER_PAGE);
        const rowInPage = el.y % A4_ROWS_PER_PAGE;

        // If widget is dragged into header
        if (rowInPage < A4_CONTENT_START_ROW) {
            return {
                ...el,
                y: pageIndex * A4_ROWS_PER_PAGE + A4_CONTENT_START_ROW,
            };
        }

        // If widget is dragged into footer
        if (rowInPage > A4_CONTENT_END_ROW) {
            return {
                ...el,
                y: (pageIndex + 1) * A4_ROWS_PER_PAGE + A4_CONTENT_START_ROW,
            };
        }

        // If widget height overflows content area
        if (rowInPage + el.h - 1 > A4_CONTENT_END_ROW) {
            return {
                ...el,
                y: (pageIndex + 1) * A4_ROWS_PER_PAGE + A4_CONTENT_START_ROW,
            };
        }

        return el;
    });

export const withDashboardFrame = (layout: GridLayoutElement[]): GridLayoutElement[] => {
    const userElements = layout.filter((el) => !isDashboardFrame(el.i, el.type));

    const maxRow = Math.max(0, ...userElements.map((el) => el.y + el.h));
    const totalPages = Math.max(1, Math.ceil(maxRow / A4_ROWS_PER_PAGE)); // at least 1 page

    const cols = COLUMNS.A4;

    const headersAndFooters: GridLayoutElement[] = [];

    for (let page = 0; page < totalPages; page += 1) {
        const baseY = page * A4_ROWS_PER_PAGE;

        headersAndFooters.push(
            {
                i: `__a4_header_${page}`,
                x: 0,
                y: baseY,
                w: cols,
                h: A4_HEADER_ROWS,
                type: LayoutElementType.A4_HEADER,
                static: true,
                isDraggable: false,
                isResizable: false,
                minWidth: cols,
                maxWidth: cols,
                minHeight: A4_HEADER_ROWS,
                maxHeight: A4_HEADER_ROWS,
            },
            {
                i: `__a4_footer_${page}`,
                x: 0,
                y: baseY + A4_CONTENT_END_ROW + 1,
                w: cols,
                h: A4_FOOTER_ROWS,
                type: LayoutElementType.A4_FOOTER,
                static: true,
                isDraggable: false,
                isResizable: false,
                minWidth: cols,
                maxWidth: cols,
                minHeight: A4_FOOTER_ROWS,
                maxHeight: A4_FOOTER_ROWS,
            }
        );
    }

    return [...headersAndFooters, ...userElements];
};
